import { FC, useState, useEffect } from 'react';
import {
  type ObjectiveMedia,
  useGetUserMediaRatingQuery,
  useSubmitMediaRatingMutation,
  useGetCurrentUserQuery,
} from '@teachur/http-client';

import { Video } from '@new/common/ui/Video/Video';
import { StarRating } from '@new/common/ui/StarRating/StarRating';
import { getMediaType, isValidUrl } from '@new/features/objectives/lib/mediaHelpers';
import { ReportButton } from '../FeedbackButton/ReportButton';
import { ReportModal } from '../FeedbackModal/ReportModal';
import Embedly from '@components/files/Embedly';

interface MediaCardProps {
  media: ObjectiveMedia;
  objectiveName?: string;
  objectiveId: number;
  courseName?: string;
}

export const MediaCard: FC<MediaCardProps> = ({
  media,
  objectiveName,
  objectiveId,
  courseName,
}) => {
  const mediaType = getMediaType(media);
  const isVideo = mediaType === 'video';
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [rating, setRating] = useState(0);

  const { data: userRating } = useGetUserMediaRatingQuery(objectiveId, media.id);
  const { data: currentUser } = useGetCurrentUserQuery();
  const { mutate: submitRating } = useSubmitMediaRatingMutation();

  useEffect(() => {
    if (userRating !== undefined) {
      setRating(userRating);
    }
  }, [userRating]);

  const handleRatingChange = (newRating: number) => {
    setRating(newRating);
    if (objectiveId) {
      submitRating({
        objectiveId,
        mediaId: media.id,
        rating: newRating,
        mediaUrl: media.url,
        fileUrl: media.file?.url,
        objectiveName: objectiveName || '',
        courseName: courseName || '',
        mediaDescription: media.description || '',
        userId: currentUser?.id,
        userName: currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : undefined,
        userEmail: currentUser?.email,
      });
    }
  };

  if (isVideo && !isValidUrl(media.url)) return null;

  return (
    <div className="relative flex flex-col gap-4 border-b">
      {/* Video/Embedly Preview */}
      {isVideo && media.url ? (
        <Video videoUrl={media.url} />
      ) : media.url ? (
        <Embedly data={{ url: media.url, description: media.description || media.url }} />
      ) : null}
      {media.file?.url && (
        <>
          {isVideo ? (
            <Video videoUrl={media.file.url} />
          ) : (
            <Embedly
              data={{ url: media.file.url, description: media.description || media.file.url }}
            />
          )}
        </>
      )}

      <div className="flex flex-col gap-3 p-4">
        {isVideo && (
          <p className="text-(--neutral-black-NB900)! font-[Montserrat] text-[20px]! font-normal leading-[150%] m-0!">
            {media.description || 'No description available.'}
          </p>
        )}

        {media.time && (
          <p className="text-(--neutral-white-NW600) text-sm m-0!">
            <b>Time: </b>
            {media.time} minutes
          </p>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Rate this media:</span>
            <StarRating value={rating} onChange={handleRatingChange} />
          </div>
          <div onClick={() => setIsFeedbackModalOpen(true)}>
            <ReportButton />
          </div>
        </div>
      </div>

      <ReportModal
        open={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
        media={media}
        objectiveName={objectiveName}
        objectiveId={objectiveId}
        rating={rating}
        courseName={courseName}
      />
    </div>
  );
};
